import { fetchDxApi, fetchDxApiAuthenticated } from "./fetchStudioApi";
import { DXAppBlueprint } from "../../data/DXApp/DXAppBlueprint";
import { getCachedRequest } from "./requestCache";

export interface CreateAppRequest {
    title: string;
    storefront_slug: string;
    app_slug: string;
    description: string;
    theme?: {
        themeSlug: string;
        primaryColor: string;
        secondaryColor: string;
        backgroundColor?: string;
    }
}

export interface AppIdeaRequest {
    appIdea: string;
}

export interface CreateAppResponse {
    app: any;
    landing_page: any;
    onboarding_flow: any;
    pricing: any;
    storefront: any;
    insights_library: any;
    canvas_settings?: {
        canvas_mode?: string;
        landing_page_settings?: {
            view?: string;
            is_view_toggle_visible?: boolean;
        };
        is_mode_bar_visible?: boolean;
        editor_settings?: {
            is_simulator_visible?: boolean;
        };
    };
    generation_complete?: boolean;
}

export interface CreateBrainstormRequest {
    userContext: string;
}

export interface CreateBrainstormResponse {
    brainstorm: any;
    storefront: any;
    canvas_settings?: {
        canvas_mode?: string;
        is_mode_bar_visible?: boolean;
    };
}

export const createApp = async (appData: CreateAppRequest | AppIdeaRequest): Promise<CreateAppResponse> => {
    try {

        // First try with authentication headers if user is logged in
        const response = await fetchDxApiAuthenticated({
            path: '/appBuilder/buildApp',
            method: 'POST',
            body: JSON.stringify(appData),
            isJsonPayload: true
        });
        return response.response;
    } catch (error) {
        // If authentication fails or user is not logged in, try with non-auth version
        const response = await fetchDxApi({
            path: '/appBuilder/buildApp',
            method: 'POST',
            body: JSON.stringify(appData),
            isJsonPayload: true
        });
        return response.response;
    }
};

export const createBrainstorm = async (brainstormData: CreateBrainstormRequest): Promise<CreateBrainstormResponse> => {
    console.log('[DXAppBuilderApi] Creating brainstorm with data:', brainstormData);

    try {
        const response = await fetchDxApiAuthenticated({
            path: '/appBuilder/buildBrainstorm',
            method: 'POST',
            body: JSON.stringify(brainstormData),
            isJsonPayload: true
        });
        console.log('[DXAppBuilderApi] Brainstorm created successfully:', response);
        return response.response;
    } catch (error) {
        console.log('[DXAppBuilderApi] Auth failed, trying non-auth request...');
        // Fallback to non-auth request
        const response = await fetchDxApi({
            path: '/appBuilder/buildBrainstorm',
            method: 'POST',
            body: JSON.stringify(brainstormData),
            isJsonPayload: true
        });
        console.log('[DXAppBuilderApi] Brainstorm created successfully (non-auth):', response);
        return response.response;
    }
};

// New API endpoints for blueprint functionality

export const getCreatorApps = async (withLandingPageInfo: boolean = false, publishedOnly: boolean = false): Promise<any[]> => {
    try {
        // Build query parameters
        const params = new URLSearchParams();
        if (withLandingPageInfo) {
            params.append('with_landing_page_info', 'true');
        }
        if (publishedOnly) {
            params.append('published_only', 'true');
        }

        const queryString = params.toString();
        const path = queryString ? `/appBuilder/getCreatorApps?${queryString}` : "/appBuilder/getCreatorApps";

        const response = await fetchDxApiAuthenticated({
            path: path,
            method: 'GET',
            isJsonPayload: false
        });

        return response.response.apps || [];
    } catch (error) {
        // Fallback to non-auth request
        try {
            const params = new URLSearchParams();
            if (withLandingPageInfo) {
                params.append('with_landing_page_info', 'true');
            }
            if (publishedOnly) {
                params.append('published_only', 'true');
            }

            const queryString = params.toString();
            const path = queryString ? `/appBuilder/getCreatorApps?${queryString}` : "/appBuilder/getCreatorApps";

            const response = await fetchDxApi({
                path: path,
                method: 'GET',
                isJsonPayload: false
            });
            return response.response.apps || [];
        } catch (fallbackError) {
            console.error('Error fetching creator apps:', fallbackError);
            return [];
        }
    }
};

// Backwards compatibility alias
export const getUserApps = getCreatorApps;

export const updateRecentlyOpened = async (appId: string): Promise<any> => {
    try {
        const response = await fetchDxApiAuthenticated({
            path: `/appBuilder/updateRecentlyOpened?appId=${appId}`,
            method: 'POST',
            isJsonPayload: false
        });
        return response.response;
    } catch (error) {
        console.error("Failed to update recently opened timestamp:", error);
        return null;
    }
};

export const getBlueprintForApp = async (appId: string): Promise<any> => {

    console.log("🔍 Calling the blueprint for app in getBlueprintForApp", appId);
    try {
        const response = await fetchDxApiAuthenticated({
            path: `/appBuilder/getBlueprintForApp?appId=${appId}`,
            method: 'GET',
            isJsonPayload: false
        });

        return response.response;
    } catch (error) {
        // Fallback to non-auth request

        const response = await fetchDxApi({
            path: `/appBuilder/getBlueprintForApp?appId=${appId}`,
            method: 'GET',
            isJsonPayload: false
        });

        return response.response;
    }
};

export const updateBlueprint = async (blueprint: any): Promise<any> => {

    try {
        const response = await fetchDxApiAuthenticated({
            path: '/appBuilder/updateBlueprint',
            method: 'POST',
            body: JSON.stringify(blueprint),
            isJsonPayload: true
        });

        return response.response;
    } catch (error) {
        // Fallback to non-auth request

        const response = await fetchDxApi({
            path: '/appBuilder/updateBlueprint',
            method: 'POST',
            body: JSON.stringify(blueprint),
            isJsonPayload: true
        });

        return response.response;
    }
};

export const getSimulatorItemById = async (itemId: string): Promise<any> => {

    try {
        const response = await fetchDxApiAuthenticated({
            path: `/appBuilder/getSimulatorItemById?itemId=${itemId}`,
            method: 'GET',
            isJsonPayload: false
        });

        return response.response;
    } catch (error) {

        // Fallback to non-auth request
        const response = await fetchDxApi({
            path: `/appBuilder/getSimulatorItemById?itemId=${itemId}`,
            method: 'GET',
            isJsonPayload: false
        });

        return response.response;
    }
};

export const getMostRecentSimulatorItemByTypeAndAppId = async (appId: string, itemType: string): Promise<any> => {


    // Use cache key to deduplicate requests
    const cacheKey = `simulator-item-${appId}-${itemType}`;

    return getCachedRequest(cacheKey, async () => {
        try {
            const response = await fetchDxApiAuthenticated({
                path: `/appBuilder/getMostRecentSimulatorItemByTypeAndAppId?appId=${appId}&itemType=${itemType}`,
                method: 'GET',
                isJsonPayload: false
            });

            return response.response;
        } catch (error) {

            // Fallback to non-auth request
            const response = await fetchDxApi({
                path: `/appBuilder/getMostRecentSimulatorItemByTypeAndAppId?appId=${appId}&itemType=${itemType}`,
                method: 'GET',
                isJsonPayload: false
            });

            return response.response;
        }
    });
};

// New function to get all simulator items for an app with pagination
export const getSimulatorItemsForApp = async (
    appId: string,
    page: number = 1,
    limit: number = 10,
    types?: string[]
): Promise<{ items: any[], hasMore: boolean, total: number }> => {


    // Build query parameters
    const queryParams = new URLSearchParams();
    queryParams.append('appId', appId);
    queryParams.append('page', String(page));
    queryParams.append('limit', String(limit));

    // Add types filter if provided
    if (types && types.length > 0) {
        queryParams.append('types', types.join(','));
    }

    // Use cache key to deduplicate requests
    const cacheKey = `simulator-items-${appId}-${page}-${limit}-${types?.join(',') || 'all'}`;

    return getCachedRequest(cacheKey, async () => {
        try {
            // Try authenticated request first
            const response = await fetchDxApiAuthenticated({
                path: `/appBuilder/getSimulatorItemsForApp?${queryParams.toString()}`,
                method: 'GET',
                isJsonPayload: false
            });


            if (response.response) {
                return response.response;
            }

            return { items: [], hasMore: false, total: 0 };
        } catch (error) {


            try {
                // Fallback to non-auth request
                const response = await fetchDxApi({
                    path: `/appBuilder/getSimulatorItemsForApp?${queryParams.toString()}`,
                    method: 'GET',
                    isJsonPayload: false
                });


                if (response.response) {
                    return response.response;
                }

                return { items: [], hasMore: false, total: 0 };
            } catch (error) {

                return { items: [], hasMore: false, total: 0 };
            }
        }
    }, 10000); // Cache for 10 seconds since this is paginated data
};

// New function to save a blueprint example item
export const saveBlueprintExample = async (exampleData: {
    appId: string;
    type: string;
    title: string;
    content: any;
    simId?: string;
}): Promise<any> => {


    try {
        const response = await fetchDxApiAuthenticated({
            path: '/appBuilder/saveBlueprintExample',
            method: 'POST',
            body: JSON.stringify(exampleData),
            isJsonPayload: true
        });

        return response.response;
    } catch (error) {

        // Fallback to non-auth request
        const response = await fetchDxApi({
            path: '/appBuilder/saveBlueprintExample',
            method: 'POST',
            body: JSON.stringify(exampleData),
            isJsonPayload: true
        });

        return response.response;
    }
};

// Function to get blueprint examples for an app
export const getBlueprintExamplesForApp = async (appId: string, type?: string): Promise<any[]> => {


    // Build query parameters
    const queryParams = new URLSearchParams();
    queryParams.append('appId', appId);
    if (type) {
        queryParams.append('type', type);
    }

    try {
        const response = await fetchDxApiAuthenticated({
            path: `/appBuilder/getBlueprintExamplesForApp?${queryParams.toString()}`,
            method: 'GET',
            isJsonPayload: false
        });

        return response.response?.examples || [];
    } catch (error) {

        // Fallback to non-auth request
        const response = await fetchDxApi({
            path: `/appBuilder/getBlueprintExamplesForApp?${queryParams.toString()}`,
            method: 'GET',
            isJsonPayload: false
        });

        return response.response?.examples || [];
    }
};

// Function to get a blueprint example by ID
export const getBlueprintExampleById = async (exampleId: string): Promise<any> => {


    try {
        const response = await fetchDxApiAuthenticated({
            path: `/appBuilder/getBlueprintExampleById?exampleId=${exampleId}`,
            method: 'GET',
            isJsonPayload: false
        });

        return response.response?.example;
    } catch (error) {

        // Fallback to non-auth request
        const response = await fetchDxApi({
            path: `/appBuilder/getBlueprintExampleById?exampleId=${exampleId}`,
            method: 'GET',
            isJsonPayload: false
        });

        return response.response?.example;
    }
};

// Function to regenerate a specific landing page image
export const regenerateLandingPageImage = async (
    appId: string,
    imageType: 'hero' | 'benefits' | 'features' | 'creator',
    specificInstructions?: string
): Promise<any> => {


    const requestData = {
        appId,
        imageType,
        specificInstructions
    };

    try {
        const response = await fetchDxApiAuthenticated({
            path: '/appBuilder/regenerateLandingPageImage',
            method: 'POST',
            body: JSON.stringify(requestData),
            isJsonPayload: true
        });

        return response.response;
    } catch (error) {

        // Fallback to non-auth request
        const response = await fetchDxApi({
            path: '/appBuilder/regenerateLandingPageImage',
            method: 'POST',
            body: JSON.stringify(requestData),
            isJsonPayload: true
        });

        return response.response;
    }
};

// Function to get all media items for an app
export const getAppMedia = async (appId: string): Promise<any[]> => {


    try {
        const response = await fetchDxApiAuthenticated({
            path: `/appBuilder/getAppMedia?appId=${appId}`,
            method: 'GET',
            isJsonPayload: false
        });

        return response.response?.media || [];
    } catch (error) {

        // Fallback to non-auth request
        const response = await fetchDxApi({
            path: `/appBuilder/getAppMedia?appId=${appId}`,
            method: 'GET',
            isJsonPayload: false
        });

        return response.response?.media || [];
    }
};

// Function to save a media item for an app
export const saveAppMedia = async (
    appId: string,
    type: string,
    url: string
): Promise<any> => {


    const requestData = {
        appId,
        type,
        url
    };

    try {
        const response = await fetchDxApiAuthenticated({
            path: '/appBuilder/saveAppMedia',
            method: 'POST',
            body: JSON.stringify(requestData),
            isJsonPayload: true
        });

        return response.response;
    } catch (error) {

        // Fallback to non-auth request
        const response = await fetchDxApi({
            path: '/appBuilder/saveAppMedia',
            method: 'POST',
            body: JSON.stringify(requestData),
            isJsonPayload: true
        });

        return response.response;
    }
};

// Function to get landing page version history for an app
export const getLandingPageVersionHistory = async (appId: string): Promise<any[]> => {


    try {
        const response = await fetchDxApiAuthenticated({
            path: `/appBuilder/getLandingPageVersionHistory?appId=${appId}`,
            method: 'GET',
            isJsonPayload: false
        });

        return response.response?.versions || [];
    } catch (error) {

        // Fallback to non-auth request
        const response = await fetchDxApi({
            path: `/appBuilder/getLandingPageVersionHistory?appId=${appId}`,
            method: 'GET',
            isJsonPayload: false
        });

        return response.response?.versions || [];
    }
};

// Function to get blueprint version history for an app
export const getBlueprintVersionHistory = async (appId: string): Promise<any[]> => {


    try {
        const response = await fetchDxApiAuthenticated({
            path: `/appBuilder/getBlueprintVersionHistory?appId=${appId}`,
            method: 'GET',
            isJsonPayload: false
        });

        return response.response?.versions || [];
    } catch (error) {

        // Fallback to non-auth request
        const response = await fetchDxApi({
            path: `/appBuilder/getBlueprintVersionHistory?appId=${appId}`,
            method: 'GET',
            isJsonPayload: false
        });

        return response.response?.versions || [];
    }
};

// Function to restore a specific landing page version
export const restoreLandingPageVersion = async (
    appId: string,
    versionId: string
): Promise<any> => {


    const requestData = {
        appId,
        versionId
    };

    try {
        const response = await fetchDxApiAuthenticated({
            path: '/appBuilder/restoreLandingPageVersion',
            method: 'POST',
            body: JSON.stringify(requestData),
            isJsonPayload: true
        });

        return response.response;
    } catch (error) {

        // Fallback to non-auth request
        const response = await fetchDxApi({
            path: '/appBuilder/restoreLandingPageVersion',
            method: 'POST',
            body: JSON.stringify(requestData),
            isJsonPayload: true
        });

        return response.response;
    }
};

// Function to restore a specific blueprint version
export const restoreBlueprintVersion = async (
    appId: string,
    versionId: string
): Promise<any> => {


    const requestData = {
        appId,
        versionId
    };

    try {
        const response = await fetchDxApiAuthenticated({
            path: '/appBuilder/restoreBlueprintVersion',
            method: 'POST',
            body: JSON.stringify(requestData),
            isJsonPayload: true
        });

        return response.response;
    } catch (error) {

        // Fallback to non-auth request
        const response = await fetchDxApi({
            path: '/appBuilder/restoreBlueprintVersion',
            method: 'POST',
            body: JSON.stringify(requestData),
            isJsonPayload: true
        });

        return response.response;
    }
};

// Function to update app canvas settings
export const updateAppCanvasSettings = async (
    appId: string,
    canvasSettings: any
): Promise<any> => {


    const requestData = {
        appId,
        canvasSettings
    };

    try {
        const response = await fetchDxApiAuthenticated({
            path: '/apps/updateApp',
            method: 'POST',
            body: JSON.stringify(requestData),
            isJsonPayload: true
        });

        return response.response;
    } catch (error) {

        // Fallback to non-auth request
        const response = await fetchDxApi({
            path: '/apps/updateApp',
            method: 'POST',
            body: JSON.stringify(requestData),
            isJsonPayload: true
        });

        return response.response;
    }
};

// Function to edit landing page content based on user prompt
export const editLandingPage = async (editData: {
    appId: string;
    userPrompt: string;
    blueprint: string;
    creatorId?: string;
}): Promise<any> => {
    console.log(`API call: editLandingPage with data:`, editData);

    try {
        const response = await fetchDxApiAuthenticated({
            path: '/appBuilder/edit-landing-page',
            method: 'POST',
            body: JSON.stringify(editData),
            isJsonPayload: true
        });
        console.log("API response from authenticated call:", response);
        return response.response;
    } catch (error) {
        console.log("Authenticated call failed, trying non-auth request:", error);
        // Fallback to non-auth request
        const response = await fetchDxApi({
            path: '/appBuilder/edit-landing-page',
            method: 'POST',
            body: JSON.stringify(editData),
            isJsonPayload: true
        });
        console.log("API response from non-auth call:", response);
        return response.response;
    }
};
